import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, Settings, Activity, MessageSquare, User as UserIcon, Cpu, Target, Download } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';

interface DashboardStats {
  totalUsers: number;
  totalReports: number;
  activeUsers: number;
  recentActivity: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalReports: 0,
    activeUsers: 0,
    recentActivity: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);

      const { count: usersCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      const { count: reportsCount } = await supabase
        .from('reports')
        .select('*', { count: 'exact', head: true });

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { count: activeUsersCount } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .gte('updated_at', thirtyDaysAgo.toISOString());

      const { count: recentReportsCount } = await supabase
        .from('reports')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      setStats({
        totalUsers: usersCount || 0,
        totalReports: reportsCount || 0,
        activeUsers: activeUsersCount || 0,
        recentActivity: recentReportsCount || 0
      });
    } catch (error: any) {
      console.error('Error loading dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAll = async () => {
    try {
      const { exportAllConfigurations } = await import('../../utils/configurationExporter');
      toast.info('Exporting all configurations... This may take a moment.');

      const result = await exportAllConfigurations();

      if (result.success) {
        const successCount = result.results.filter(r => r.success).length;
        toast.success(`Successfully exported ${successCount} configuration files!`);
      } else {
        const failedExports = result.results.filter(r => !r.success);
        toast.error(`Failed to export ${failedExports.length} files. Check console for details.`);
      }
    } catch (error: any) {
      console.error('Error exporting configurations:', error);
      toast.error('Failed to export configurations');
    }
  };

  const handleExportContextVariables = async () => {
    try {
      const { exportContextVariables } = await import('../../utils/configurationExporter');
      const result = await exportContextVariables();

      if (result.success) {
        toast.success(`Exported ${result.filename} successfully!`);
      } else {
        toast.error(result.error || 'Failed to export context variables');
      }
    } catch (error: any) {
      console.error('Error exporting:', error);
      toast.error('Failed to export context variables');
    }
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-blue-500',
      link: '/admin/users'
    },
    {
      title: 'Total Reports',
      value: stats.totalReports,
      icon: FileText,
      color: 'bg-green-500',
      link: '/admin/reports'
    },
    {
      title: 'Active Users (30d)',
      value: stats.activeUsers,
      icon: Activity,
      color: 'bg-purple-500',
      link: '/admin/users'
    },
    {
      title: 'Recent Reports (30d)',
      value: stats.recentActivity,
      icon: FileText,
      color: 'bg-orange-500',
      link: '/admin/reports'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Manage users, reports, and system settings</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleExportContextVariables}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Context Variables
            </Button>
            <Button
              onClick={handleExportAll}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export All Configurations
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.title}
              to={card.link}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${card.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{card.title}</h3>
              <p className="text-3xl font-bold text-gray-900">{card.value}</p>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            User Management
          </h2>
          <p className="text-gray-600 mb-4">
            View and manage all registered users, send invitations, and control admin access.
          </p>
          <Link
            to="/admin/users"
            className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Manage Users
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Reports Management
          </h2>
          <p className="text-gray-600 mb-4">
            View all generated reports, monitor generation status, and access user reports.
          </p>
          <Link
            to="/admin/reports"
            className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            View Reports
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2" />
            Prompt Management
          </h2>
          <p className="text-gray-600 mb-4">
            Create and manage AI prompts, configure execution order, and dependencies.
          </p>
          <Link
            to="/admin/prompts"
            className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Manage Prompts
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <UserIcon className="w-5 h-5 mr-2" />
            Persona Management
          </h2>
          <p className="text-gray-600 mb-4">
            Define AI personas with unique characteristics and communication styles.
          </p>
          <Link
            to="/admin/personas"
            className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Manage Personas
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Target className="w-5 h-5 mr-2" />
            Job Match Quality Control
          </h2>
          <p className="text-gray-600 mb-4">
            Monitor AI analysis quality scores, validation results, and review flagged analyses.
          </p>
          <Link
            to="/admin/job-match-quality"
            className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            View Quality Dashboard
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Cpu className="w-5 h-5 mr-2" />
            LLM Configuration
          </h2>
          <p className="text-gray-600 mb-4">
            Configure language models, sync available models, and manage AI parameters.
          </p>
          <Link
            to="/admin/llm"
            className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Configure LLMs
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2" />
            User Feedback
          </h2>
          <p className="text-gray-600 mb-4">
            View and manage user feedback, bug reports, and feature requests.
          </p>
          <Link
            to="/admin/feedback"
            className="inline-block bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            View Feedback
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
